import React, { useState } from 'react';
import { OV_STRINGS } from '../../constants/strings';
import { getPhotoUrl } from '../../../../common/utils/getPhotoUrl';
import { E_RAND_TYPE_LABEL, DEAL_TYPE_NAMES } from '../../../../common/constants/saleConstants';
import { priceFormat } from '../../../../utils/format';
import * as legacy from '../../../../utils/legacy';
import styles from './OwnerVerificationConfirmModal.module.scss';


function getConInfo(estate) {
  const to_area = legacy.to_area;
  const type = estate.e_rand_type;
  if (type === 16) {
    return [
      to_area(estate.land_area),
      legacy.ifo_get_land_purpose_type(estate.land_purpose),
    ].filter(Boolean).join(' ∙ ');
  }
  if (
    [0, 1, 2, 7, 8, 10, 11, 18].includes(type) ||
    ([4, 6].includes(type) && estate.deal_type !== 0)
  ) {
    const areaNm = [2, 7, 8, 10, 11, 18].includes(type) ? '계약' : '공급';
    let floor = '';
    if (estate.floor_type == null || estate.floor_type < 0) {
      floor = `${estate.floor_cnt} / ${estate.floor_total}`;
    } else {
      if (estate.floor_type === 0) floor = '고';
      if (estate.floor_type === 1) floor = '중';
      if (estate.floor_type === 2) floor = '저';
    }
    return `${areaNm} ${to_area(estate.supply_area)} ∙ 전용 ${to_area(estate.floor_area)} ∙ ${floor}층`;
  }
  return `대지 ${to_area(estate.land_area)} ∙ 연 ${to_area(estate.floor_area)} ∙ ${estate.floor_cnt}F / B${estate.under_floor}`;
}

function OwnerVerificationConfirmModal({
  isOpen = false, estate = {}, ticketCount = 0,
  onStart, onCharge, onClose,
}) {
  const [selectedPeriod, setSelectedPeriod] = useState(0); // 0=1개월, 1=2개월, 2=3개월
  const requiredTickets = selectedPeriod + 1;
  const remaining = ticketCount - requiredTickets;
  const hasEnough = remaining >= 0;

  if (!isOpen) return null;

  const thumbUrl = getPhotoUrl(estate.photo, 'building');
  const dealName = DEAL_TYPE_NAMES[estate.deal_type] ?? '';
  const typeName = E_RAND_TYPE_LABEL[estate.e_rand_type] ?? '';
  const price = estate.price
    ? priceFormat(estate.price)
    : estate.monthly > 0
      ? `${priceFormat(estate.deposit)} / ${priceFormat(estate.monthly)}`
      : priceFormat(estate.deposit);

  return (
    <>
      <div className={styles.dim} onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }} />
      <div className={styles.pos}>
        <div className={styles.modal}>

          <div className={styles.nav}>
            <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="닫기">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="#171719" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className={styles.thumb}>
            <div className={styles.thumbBg} />
            <div className={styles.card}>
              <div className={styles.cardRow}>
                <div className={styles.cardPhoto}>
                  {thumbUrl ? <img src={thumbUrl} alt="" /> : <div className={styles.cardPhotoEmpty} />}
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.cardTop}>
                    <div className={styles.cardBadges}>
                      {typeName && <span className={styles.badgePurple}>{typeName}</span>}
                    </div>
                    {estate.update_date && <span className={styles.cardDate}>{estate.update_date}</span>}
                  </div>
                  {estate.address && <p className={styles.cardAddr}>{estate.address}</p>}
                  <div className={styles.cardPriceRow}>
                    {dealName && <span className={styles.cardDeal}>{dealName}</span>}
                    {price && price !== '-' && <span className={styles.cardPrice}>{price}</span>}
                  </div>
                  {(estate.land_area > 0 || estate.floor_area > 0 || estate.supply_area > 0) && (
                    <div className={styles.cardMeta} dangerouslySetInnerHTML={{ __html: getConInfo(estate) }} />
                  )}
                </div>
              </div>
              <div className={styles.cardDivider} />
            </div>
          </div>


          <div className={styles.content}>
            <div className={styles.heading}>
              <h2 className={styles.title}>{OV_STRINGS.CONFIRM_TITLE}</h2>
              <p className={styles.desc}>{OV_STRINGS.CONFIRM_DESC}</p>
            </div>

            <div className={styles.periodSection}>
              <span className={styles.periodLabel}>{OV_STRINGS.PERIOD_LABEL}</span>
              <div className={styles.segmented}>
                {OV_STRINGS.PERIOD_OPTIONS.map((label, i) => (
                  <button
                    key={i}
                    type="button"
                    className={selectedPeriod === i ? styles.segmentTabActive : styles.segmentTab}
                    onClick={() => setSelectedPeriod(i)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.ticketCard}>
              <div className={styles.ticketHeader}>
                <span className={styles.ticketHeaderLabel}>{OV_STRINGS.TICKET_HEADER}</span>
                <button type="button" className={styles.ticketChargeBtn} onClick={onCharge}>
                  {OV_STRINGS.CHARGE_LINK}
                  <span className={styles.ticketChargeChevron}>›</span>
                </button>
              </div>
              <div className={styles.ticketRow}>
                <span className={styles.ticketRowLabel}>{OV_STRINGS.OWNED_TICKETS_LABEL}</span>
                <span className={styles.ticketRowValue}>{ticketCount}{OV_STRINGS.TICKET_UNIT}</span>
              </div>
              <div className={styles.ticketRow}>
                <span className={styles.ticketRowLabel}>
                  {OV_STRINGS.REQUIRED_TICKETS_LABEL}{' '}
                  <span className={styles.ticketRowLabelSub}>({OV_STRINGS.PERIOD_OPTIONS[selectedPeriod]})</span>
                </span>
                <span className={styles.ticketRowValueRed}>{requiredTickets}{OV_STRINGS.TICKET_UNIT}</span>
              </div>
              <div className={styles.ticketDivider} />
              <div className={styles.ticketTotalRow}>
                <span>{OV_STRINGS.REMAINING_TICKETS_LABEL}</span>
                {hasEnough
                  ? <span>{remaining}{OV_STRINGS.TICKET_UNIT}</span>
                  : <span className={styles.ticketTotalValueNeutral}>{OV_STRINGS.INSUFFICIENT_TICKETS}</span>
                }
              </div>
            </div>
          </div>


          <div className={styles.actionArea}>
            <button
              type="button"
              className={hasEnough ? styles.cta : styles.ctaDisabled}
              disabled={!hasEnough}
              onClick={() => hasEnough && onStart?.(requiredTickets)}
            >
              {OV_STRINGS.START_BUTTON}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default OwnerVerificationConfirmModal;
